<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card elevation="2">
          <v-toolbar>
            <v-toolbar-title>{{ t('Tus_reservas') }}</v-toolbar-title>
          </v-toolbar>
          <!-- Use the template ref -->
          <ReservationList ref="resList" @reservation-cancelled="handleCancellation" />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
  <v-container>
    <v-row>
      <v-col cols="12" md="6">
        <v-card elevation="2">
          <v-toolbar>
            <v-toolbar-title>1. {{ t('Selecciona_un_simulador') }}</v-toolbar-title>
            <v-btn v-if="profesionalesDisponibles.length > 1" absolute location="top right" icon @click="openFiltratge">
              <v-icon>{{ profesionalesSeleccionados.length > 0 ? 'mdi-filter' : 'mdi-filter-off' }}</v-icon>
            </v-btn>
          </v-toolbar>
          <v-list>
            <v-list-item v-for="combinacion in combinacionesCursoSimulador" :key="combinacion.clave"
              :value="combinacion" color="primary"
              @click="selectCombinacion(combinacion.cursoId, combinacion.simuladorId)">
              <v-list-item-title>{{ combinacion.nombreSimulador }}</v-list-item-title>
              <v-list-item-subtitle>{{ combinacion.nombreCurso }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <!-- Selector de Mes y Calendario -->
      <v-col v-if="selectedSimulator" cols="12" md="6">
        <v-card elevation="2">
          <v-toolbar>
            <v-toolbar-title>2. {{ t('Elige_un_día') }}</v-toolbar-title>
          </v-toolbar>
          <v-row justify="center" align="center">
            <v-col cols="12" sm="6" class="d-flex justify-center align-center pa-5">
              <v-skeleton-loader v-if="isLoading" type="card" height="270" width="270"></v-skeleton-loader>
              <Calendar v-if="!isLoading" v-model:initial-page="selectedMonth" :attributes="formattedEvents" @update:pages="pageChange"
                @dayclick="selectDate" />
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col cols="12" v-if="selectedDate">
        <v-card elevation="2">
          <v-toolbar>
            <v-toolbar-title style="word-break: break-word;" class="text-wrap">3. {{ t('Reservas_disponibles') }} {{
              selectedDate.toLocaleDateString() }}</v-toolbar-title>
          </v-toolbar>
          <v-list v-if="selectedDayTimeSlots.length > 0" lines="two">
            <v-list-item v-for="slot in selectedDayTimeSlots" :key="slot.id">
              <v-list-item-title>
                {{ formatTime(slot.inicio) }} a {{ formatTime(slot.fin) }}
                <span v-if="slot.alumnos && slot.alumnos.length >= selectedSimulatorData?.num_alumnos">
                  ({{ t('reservado') }})
                </span>
                <span v-else>({{ slotAvailability(slot) }})</span>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ selectedSimulatorData?.nombre }}
                <span v-if="slot.curso">({{ slot.curso.titulo }})</span>
                - {{ selectedDate.toLocaleDateString() }}
              </v-list-item-subtitle>
              <template v-slot:append>
                <span v-if="slot.alumnos && slot.alumnos.length >= selectedSimulatorData?.num_alumnos">
                  <v-btn small disabled class="ma-2">
                    <v-icon>mdi-calendar-plus</v-icon>
                    <span class="d-none d-sm-inline">{{ t('reservado') }}</span>
                  </v-btn>
                </span>
                <span v-else>
                  <v-btn small color="green" class="ma-2" @click="reservarSlot(slot.id)" v-if="!isSlotInPast(slot)">
                    <v-icon>mdi-calendar-plus</v-icon>
                    <span class="d-none d-sm-inline">{{ t('Reservar') }}</span>
                  </v-btn>
                </span>
              </template>
            </v-list-item>
          </v-list>
          <v-alert v-else color="info" class="ma-3">
            {{ t('No_hay_disponibilidad_de_reservas_para_el_simulador_de') }}
            {{ selectedSimulatorData?.nombre }}
            {{ t('el_día') }}
            {{ selectedDate.getDate() }}
            de
            {{ monthName(selectedDate) }}.
          </v-alert>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
  <!-- Diálogo para Seleccionar especialidades -->
  <v-dialog v-model="open" max-width="600px">
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>{{ t('Filtrar_por_especialidad') }}</v-toolbar-title>
        <v-btn color="primary" text prepend-icon="mdi-filter" @click="applyFilter">{{ t('Aplicar') }}</v-btn>
        <v-btn icon @click="open = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-select multiple return-object clearable v-model="profesionalesSeleccionados"
          :items="profesionalesDisponibles" item-title="categoria" item-value="id" :label="t('Especialidad')">
        </v-select>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
/* Imports */
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { axios } from '../axios'
import ReservationList from './ReservationList.vue'
import { Calendar } from 'v-calendar';
import 'v-calendar/style.css';

import { useUiStore } from '@/stores/ui';
import { useCursosStore } from '@/stores/cursos';
import { useUserStore }   from '@/stores/user';

const uiStore = useUiStore();  
const cursosStore = useCursosStore();
const userStore   = useUserStore();

const { t } = useI18n()

/* Template ref for ReservationList */
const resList = ref(null)

/* Reactive data equivalents */
const selectedMonth = ref({ month: new Date().getMonth() + 1, year: new Date().getFullYear() }) // Default to current month/year
const cursos          = computed(() => cursosStore.cursos);
const loadingprof     = computed(() => userStore.loadingProfesionales);
const simuladores = ref([])
const loading = ref(false);
const timeSlots = ref([])
const formattedEvents = ref([]);
const selectedCurso = ref(null)
const selectedSimulator = ref(null)
const selectedDayTimeSlots = ref([])
const selectedDate = ref(null)
const isLoading = ref(false)
const tsimuladores = ref([])

// Obtenemos el id profesional del usuario
const loadinguserDetails = computed(() => userStore.loadingUserDetails);
// Obtenemos el id profesional del usuario (si userDetails está cargado)
const userProfesional = computed(
  () => userStore.userDetails ? userStore.userDetails.profesional : null
);

// Variable reactiva para la selección de profesionales (objetos completos)
const profesionalesSeleccionados = ref([])
const open = ref(false)

// Computed que devuelve solo las especialidades que tengan al menos un curso asociado.
// Si los profesionales aún no están cargados, devuelve un array vacío.
const profesionalesDisponibles = computed(() => {
  if (loadingprof.value) return [];
  return userStore.profesionales.filter(prof =>
    cursosStore.cursos.some(curso =>
      curso.profesionales && curso.profesionales.includes(prof.id)
    )
  );
});

// Watcher para preseleccionar la especialidad del usuario cuando los profesionales ya estén cargados
watch(
  [userProfesional, loadingprof, profesionalesDisponibles, loadinguserDetails],
  ([nuevoValor, loadingProf, disponibles, loadingUser]) => {
    if (!loadingUser && !loadingProf && nuevoValor) {
      const profSeleccionado = disponibles.find(p => p.id === nuevoValor)
      if (profSeleccionado) {
        profesionalesSeleccionados.value = [profSeleccionado]
      }
    }
  },
  { immediate: true }
)

// Computed para obtener un array de IDs a partir de los objetos seleccionados
const selectedProfesionalIds = computed(() =>
  profesionalesSeleccionados.value.map(p => p.id)
)

// Filtrado de cursos: se muestran solo aquellos que tengan algún id presente en selectedProfesionalIds
const cursosFiltrados = computed(() => {
  if (selectedProfesionalIds.value.length === 0) return [];
  return cursosStore.cursos.filter(curso =>
    curso.profesionales?.some(id => selectedProfesionalIds.value.includes(id))
  );
});

// Determina qué listado mostrar: si hay filtro y cursos filtrados se muestran estos, sino se muestran todos los cursos
const cursosToShow = computed(() =>
  selectedProfesionalIds.value.length && cursosFiltrados.value.length
    ? cursosFiltrados.value
    : cursosStore.cursos
);

function openFiltratge() {
  open.value = true
}

function applyFilter() {
  open.value = false; // o la lógica que cierre el diálogo y aplique el filtro
}


/* Computed Properties */
const combinacionesCursoSimulador = computed(() => {
  const combos = []
  cursosToShow.value.forEach(curso => {
    curso.simuladores.forEach(simId => {
      const simulador = simuladores.value.find(s => s.id === simId)
      if (simulador) {
        combos.push({
          clave: `${curso.id}-${simulador.id}`,
          cursoId: curso.id,
          nombreCurso: curso.titulo,
          simuladorId: simulador.id,
          nombreSimulador: simulador.nombre
        })

      }
    })
  })
  return combos
})

const selectedSimulatorData = computed(() =>
  simuladores.value.find(sim => sim.id === selectedSimulator.value)
)


/* Methods (Composition Functions) */
function selectCombinacion(cursoId, simuladorId) {
  selectedCurso.value = cursoId
  selectedSimulator.value = simuladorId
}

function isSlotInPast(slot) {
  const now = new Date()
  const slotDate = new Date(slot.fin)
  return slotDate < now
}

function formatTime(timeString) {
  const dateObj = new Date(timeString)
  return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

async function fetchSimuladores() {
  loading.value = true;
  return axios.get('simuladores/')
    .then(response => {
      tsimuladores.value = response.data;
    })
    .catch(error => {
      console.error('Error fetching simuladores:', error);
    })
    .finally(() => {
      loading.value = false;
    });
}

async function fetchCursos() {
  try {
    // 1. Load all simulators.
    await fetchSimuladores()

    // 2. Build a set of simulator IDs based on store courses.
    const simuladoresIds = new Set()
    cursos.value.forEach((curso) => {
      curso.simuladores.forEach((id) => {
        simuladoresIds.add(id)
      })
    })

    // 3. Filter the full simulator list to only those the user can use.
    simuladores.value = tsimuladores.value.filter((sim) =>
      simuladoresIds.has(sim.id)
    )

  } catch (error) {
    console.error('Error al obtener simuladores o filtrar cursos:', error)
  }
}

function pageChange(e) {
  if (e[0].month !== selectedMonth.value.month) {
    console.log(`month: ${selectedMonth.value.month} -> ${e[0].month}`);
    selectedMonth.value.month = e[0].month;
    selectedMonth.value.year = e[0].year;
    fetchTimeSlots();
  }
}

function fetchTimeSlots() {
  isLoading.value = true; // Activar el estado de carga
  axios.get(`time-slots/?simulador=${selectedSimulator.value}&month=${selectedMonth.value.month}&year=${selectedMonth.value.year}`, {
  }).then(response => {
    timeSlots.value = response.data;
  }).catch(error => {
    console.error("Error al cargar los time slots:", error);
  }).finally(() => {
    selectedDate.value = null; // Reiniciar la fecha seleccionada
    selectedDayTimeSlots.value = []; // Reiniciar los time slots del día seleccionado
    isLoading.value = false; // Desactivar el estado de carga
    formatEvents();
  });
}

function formatEvents() {
  // 1. Group timeslots by their day (year, month, date).
  const groupedByDay = {}
  timeSlots.value.forEach((slot) => {
    const slotDate = new Date(slot.inicio)
    // "normalize" to year-month-day (00:00:00) so all slots on that day have same key
    const dayKey = new Date(
      slotDate.getFullYear(),
      slotDate.getMonth(),
      slotDate.getDate()
    ).toISOString()

    if (!groupedByDay[dayKey]) {
      groupedByDay[dayKey] = []
    }
    groupedByDay[dayKey].push(slot)
  })

  // 2. Create V-Calendar attributes for each grouped day.
  const newAttributes = Object.entries(groupedByDay).map(([dayISO, slots]) => {
    // Convert the string key back to a Date
    const dayDate = new Date(dayISO)

    // Use your existing logic to get the color for that day
    const color = dateChipColor(dayDate)

    // Optionally, build popover text from the timeslots in this day
    // e.g. show each time range
    let popoverLabel = slots
      .map(slot => {
        const inicioDate = new Date(slot.inicio)
        const finDate = new Date(slot.fin)
        const options = { hour: '2-digit', minute: '2-digit' }
        const inicioStr = inicioDate.toLocaleTimeString([], options)
        const finStr = finDate.toLocaleTimeString([], options)
        return `${inicioStr} - ${finStr}`
      })
      .join('\n')

    // 3. Return the attribute object expected by V-Calendar
    return {
      dates: dayDate,
      highlight: {
        color
      },
      popover: {
        // Could be a simple string or an object with more advanced structure
        label: popoverLabel
      },
      // you can store any additional data if desired
      customData: {
        daySlots: slots
      }
    }
  })

  // 4. Assign the result to your `formattedEvents` ref
  formattedEvents.value = newAttributes
}

function selectDate(payload) {
  console.log('date', payload.date);
  selectedDate.value = payload.date
  selectedDayTimeSlots.value = timeSlots.value.filter(slot => {
    const slotDate = new Date(slot.inicio)
    return (
      slotDate.getFullYear() === payload.date.getFullYear() &&
      slotDate.getMonth() === payload.date.getMonth() &&
      slotDate.getDate() === payload.date.getDate()
    )
  })
}

/**
 * Check availability for each time-slot of a given date 
 * to decide what color or status we assign in the calendar.
 */
function availabilityOfDay(date) {
  const timeSlotsOfDay = timeSlots.value.filter(slot => {
    const slotDate = new Date(slot.inicio)
    return (
      slotDate.getFullYear() === date.getFullYear() &&
      slotDate.getMonth() === date.getMonth() &&
      slotDate.getDate() === date.getDate()
    )
  })
  if (timeSlotsOfDay.length === 0) return

  return timeSlotsOfDay.map(slot => {
    const numAlumnos = selectedSimulatorData.value?.num_alumnos || 0
    const alumnosReserved = slot.alumnos ? slot.alumnos.length : 0

    if (alumnosReserved === 0) {
      return t('libre')
    } else if (alumnosReserved < numAlumnos) {
      return t('ocupación') + ' ' + alumnosReserved + '/' + numAlumnos
    } else {
      return t('reservado')
    }
  })
}

function monthName(date) {
  return date.toLocaleString(navigator.language, { month: 'long' })
}

async function reservarSlot(slotId) {
  try {
    const response = await axios.post(`reservar_time_slot/${slotId}/`, {
      curso: selectedCurso.value
    })
    uiStore.triggerSnackbar({ message: response.data.message, color: 'success' });

    // Access ReservationList's method through ref
    if (resList.value) {
      resList.value.fetchUserReservations()
    }

    // Refresh time slots & re-select date
    fetchTimeSlots()
  } catch (error) {
    // Extraemos el mensaje de error del response, si existe.
    const errorMessage = error.response && error.response.data && error.response.data.message
      ? error.response.data.message
      : error.message;
    uiStore.triggerSnackbar({ message: errorMessage, color: 'error' });
  }
}

function handleCancellation() {
  // Called when ReservationList emits `reservation-cancelled`
  fetchTimeSlots()
}


/**
 * Decide the chip color for each date in the calendar:
 *  - null date => transparent
 *  - selected date => blue
 *  - all time slots free => green
 *  - at least one slot fully reserved => red
 *  - partial availability => yellow
 *  - none of the above => gray
 */
function dateChipColor(date) {
  if (!date) return 'transparent'

  const statuses = availabilityOfDay(date) || []
  if (statuses.length === 0) {
    return 'gray'
  }

  // We'll compare strings to see if they're "ocupación" or "reservado"
  const ocupacionText = t('ocupación')
  const reservadoText = t('reservado')
  let allAvailable = true
  let anyReserved = false

  for (const status of statuses) {
    if (status.includes(ocupacionText)) {
      allAvailable = false
    }
    if (status === reservadoText) {
      allAvailable = false
      anyReserved = true
      break
    }
  }

  if (allAvailable) {
    return 'green'
  } else if (anyReserved) {
    return 'red'
  } else {
    // partial occupancy => yellow
    return 'yellow'
  }
}

function slotAvailability(slot) {
  const numAlumnos = selectedSimulatorData.value?.num_alumnos || 0
  const alumnosReserved = slot.alumnos ? slot.alumnos.length : 0

  if (alumnosReserved === 0) {
    return t('libre')
  } else if (alumnosReserved < numAlumnos) {
    return t('ocupación') + ' ' + alumnosReserved + '/' + numAlumnos
  } else {
    return t('reservado')
  }
}

/* Watchers */
watch(selectedSimulator, fetchTimeSlots);

watch(
  () => cursosStore.cursos,
  val => { if (val.length) fetchCursos(); },
  { immediate: true }
);

onMounted(fetchCursos);
</script>